/**
 * The analyst-fact dialogs' values, their checks and the requests they become (PRD-0075 R-C3, R-C4).
 * The server checks again (the schema's types, the case's documents); these catch what a form can.
 */
import type { TFunction } from 'i18next'
import type { AssertFactRequest, InvalidateFactRequest } from '@/services/api/analyst-facts'
import { ApiError } from '@/services/api/client'

/** *Pridať fakt*: every field as the form holds it. */
export interface AddFactValues {
  key: string
  value: string
  sourceText: string
  /** A case document's id, or '' for none. */
  sourceDocumentId: string
  /** YYYY-MM-DD or ''. */
  validFrom: string
  validTo: string
}

/** *Už neplatí od* (a date, a reason, a source) or *Chybné* (a reason). */
export interface MarkValues {
  /** The property marked, when the mark is on one of an entity's properties. */
  key: string
  /** YYYY-MM-DD; unused by *Chybné*. */
  date: string
  reason: string
  sourceText: string
}

export type FieldProblem = 'required' | 'beforeStart'

export const EMPTY_FACT: AddFactValues = {
  key: '',
  value: '',
  sourceText: '',
  sourceDocumentId: '',
  validFrom: '',
  validTo: '',
}

export const EMPTY_MARK: MarkValues = { key: '', date: '', reason: '', sourceText: '' }

/** Written by the platform on every entity node (`PROVENANCE_KEYS` on the server): never asserted. */
const PLATFORM_KEYS = new Set([
  'case_id',
  'document_id',
  'workflow_run_id',
  'ext_id',
  'chunk_index',
  'confidence',
  'source_filename',
  'source_document_path',
  'original_source_path',
  'name_variants',
  'is_source',
  'in_case',
  'embedding',
])

export function isFactKey(key: string): boolean {
  return key.length > 0 && !key.startsWith('_') && !PLATFORM_KEYS.has(key)
}

/** The keys a fact may be about: those the schema declares for the type, then the others it holds. */
export function factKeyOptions(
  properties: Record<string, unknown>,
  declared: readonly string[]
): string[] {
  return [...new Set([...declared, ...Object.keys(properties)])].filter(isFactKey)
}

const trimmed = (value: string): string | null => value.trim() || null

export function addFactProblems(
  values: AddFactValues
): Partial<Record<keyof AddFactValues, FieldProblem>> {
  const problems: Partial<Record<keyof AddFactValues, FieldProblem>> = {}
  if (!values.key.trim()) problems.key = 'required'
  if (!values.value.trim()) problems.value = 'required'
  if (values.validFrom && values.validTo && values.validTo < values.validFrom)
    problems.validTo = 'beforeStart'
  return problems
}

export function markProblems(
  values: MarkValues,
  needs: { date: boolean; key: boolean }
): Partial<Record<keyof MarkValues, FieldProblem>> {
  const problems: Partial<Record<keyof MarkValues, FieldProblem>> = {}
  if (needs.key && !values.key) problems.key = 'required'
  if (needs.date && !values.date) problems.date = 'required'
  if (!values.reason.trim()) problems.reason = 'required'
  return problems
}

export function toAssertRequest(values: AddFactValues): AssertFactRequest {
  return {
    key: values.key.trim(),
    value: values.value.trim(),
    source_text: trimmed(values.sourceText),
    source_document_id: values.sourceDocumentId || null,
    valid_from: values.validFrom || null,
    valid_to: values.validTo || null,
  }
}

export function toInvalidateRequest(values: MarkValues): InvalidateFactRequest {
  return {
    invalid_from: values.date,
    reason: values.reason.trim(),
    source_text: trimmed(values.sourceText),
  }
}

const ERRORS = 'analystFacts.errors'
const REFUSALS = new Set([
  'key_not_assertable',
  'invalid_properties',
  'invalid_validity',
  'unknown_document',
  'reason_required',
])

/** What went wrong, in the analyst's words: a refusal by its code, a gone target, a stale state. */
export function markErrorMessage(error: unknown, t: TFunction): string {
  if (!(error instanceof ApiError)) return t(`${ERRORS}.generic`)
  if (error.status === 403) return t(`${ERRORS}.forbidden`)
  if (error.status === 404) return t(`${ERRORS}.notFound`)
  if (error.status === 409) return t(`${ERRORS}.conflict`)
  const detail = (error.data as { detail?: unknown } | undefined)?.detail
  const code = detail && typeof detail === 'object' ? (detail as { code?: unknown }).code : null
  if (error.status === 422 && typeof code === 'string' && REFUSALS.has(code)) {
    return t(`${ERRORS}.${code}`)
  }
  return t(`${ERRORS}.generic`)
}
